import React, { useEffect, useState } from "react";
import { DatePickerFr } from "@/components/DatePickerFr";
import { api, formatApiError, fmt, fmtPiles, fmtDateTime, fmtDay } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Plus, Save, AlertTriangle, RotateCcw, Pencil, FileDown, FileText } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
} from "recharts";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import PermissionMatrix from "@/components/PermissionMatrix";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

function ResetZone() {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const doReset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-stocks");
      toast.success(`Remise à zéro effectuée (${data.movements_deleted} mouvements supprimés).`);
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-xl border-2 border-red-300 bg-red-50 p-4" data-testid="reset-zone">
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className="h-5 w-5 text-red-600" />
        <h3 className="font-display font-bold text-red-800">Zone dangereuse — Remise à zéro</h3>
      </div>
      <p className="text-sm text-red-700 mb-3">
        Supprime définitivement tout l'inventaire initial et tous les mouvements de <strong>tous les sites</strong>
        {" "}(ANNEZIN, DDO et autres). Les stocks repartent de zéro. Cette action est irréversible.
      </p>
      <AlertDialog onOpenChange={() => setConfirm("")}>
        <AlertDialogTrigger asChild>
          <Button className="h-12 bg-red-600 hover:bg-red-700 text-white font-bold tactile" data-testid="reset-stocks-button">
            <RotateCcw className="h-4 w-4 mr-2" /> Remettre tous les sites à zéro
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent data-testid="reset-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la remise à zéro</AlertDialogTitle>
            <AlertDialogDescription>
              Cette opération efface l'inventaire et l'historique complet des mouvements de tous les sites.
              Tapez <strong>REINITIALISER</strong> pour confirmer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="REINITIALISER"
            className="h-12" data-testid="reset-confirm-input" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction
              disabled={confirm !== "REINITIALISER" || busy}
              onClick={doReset}
              className="bg-red-600 hover:bg-red-700"
              data-testid="reset-confirm-button"
            >
              {busy ? "Réinitialisation…" : "Confirmer la remise à zéro"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const STATES = [{ k: "sale", label: "Sale" }, { k: "propre", label: "Propre" }];

function AchatNeufsTab() {
  const { user } = useAuth();
  const [sites, setSites] = useState([]);
  const [types, setTypes] = useState([]);
  const [site, setSite] = useState("ANNEZIN");
  const [when, setWhen] = useState("");
  const [qty, setQty] = useState({});
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [legacy, setLegacy] = useState([]);

  const localNow = () => {
    const p = Object.fromEntries(new Intl.DateTimeFormat("fr-FR", {
      timeZone: "Europe/Paris", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(new Date()).map((x) => [x.type, x.value]));
    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
  };
  const load = async () => {
    const [s, h, ini] = await Promise.all([
      api.get("/stocks"), api.get("/movements?type=ACHAT_NEUFS"), api.get("/initial-stock"),
    ]);
    setSites(s.data.sites); setTypes(s.data.types); setHistory(h.data); setLegacy(ini.data);
  };
  useEffect(() => { load(); setWhen(localNow()); }, []);

  const total = Object.values(qty).reduce((n, v) => n + (Number(v) || 0), 0);
  const save = async () => {
    if (total <= 0) return toast.error("Saisissez au moins une quantité supérieure à 0.");
    const lines = types.map((t) => ({ packaging_type: t.code, quantity: (Number(qty[t.code]) || 0) * (t.pile_size || 1) })).filter((l) => l.quantity > 0);
    setSaving(true);
    try {
      await api.post("/movements", { type: "ACHAT_NEUFS", site, date: when, lines });
      toast.success("Achat d'emballages neufs enregistré.");
      setQty({}); setWhen(localNow()); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const del = async (m) => {
    if (!window.confirm("Supprimer cet achat ?")) return;
    try { await api.delete(`/movements/${m.id}`); toast.success("Achat supprimé."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const ps = (code) => (types.find((t) => t.code === code) || {}).pile_size || 1;
  const download = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const siteName = (code) => (sites.find((x) => x.code === code) || {}).name || code;

  return (
    <div className="space-y-4" data-testid="achat-neufs-tab">
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
        Enregistrez chaque <strong>achat d'emballages neufs</strong> (propres) comme un mouvement daté : les quantités
        s'ajoutent au stock propre du site choisi à la date indiquée.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Site</Label>
          <Select value={site} onValueChange={setSite}>
            <SelectTrigger className="h-12" data-testid="achat-site-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {sites.map((x) => <SelectItem key={x.code} value={x.code}>{x.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Date de l'achat (heure de Paris)</Label>
          <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="h-12" data-testid="achat-date-input" />
        </div>
      </div>
      <div className="rounded-xl border-2 border-slate-200 bg-white p-4 space-y-3">
        {types.map((t) => (
          <div key={t.code} className="flex items-center gap-3">
            <span className="flex-1 font-semibold text-slate-700">{t.label} <span className="text-xs text-slate-400">(piles · 1 pile = {fmt(t.pile_size || 1)})</span></span>
            <Input type="number" min="0" placeholder="0" value={qty[t.code] ?? ""}
              onChange={(e) => setQty((p) => ({ ...p, [t.code]: Math.max(0, parseInt(e.target.value || "0", 10)) }))}
              className="h-12 w-40 font-data text-lg" data-testid={`achat-qty-${t.code}`} />
          </div>
        ))}
      </div>
      <Button onClick={save} disabled={saving || total <= 0} className="h-12 bg-blue-800 hover:bg-blue-900 text-white font-bold tactile" data-testid="save-achat-button">
        <Save className="h-4 w-4 mr-2" /> {saving ? "Enregistrement…" : "Enregistrer l'achat"}
      </Button>

      <div>
        <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
          <h3 className="font-display font-bold text-slate-900">Historique des achats</h3>
          <div className="flex gap-2">
            <Button variant="outline" className="h-10 border-2" onClick={() => download("/export/achats.csv", "achats_neufs.csv")} data-testid="export-achats-csv-button">
              <FileDown className="h-4 w-4 mr-2" /> CSV par mois
            </Button>
            <Button variant="outline" className="h-10 border-2" onClick={() => download("/export/achats.pdf", "achats_neufs.pdf")} data-testid="export-achats-pdf-button">
              <FileText className="h-4 w-4 mr-2" /> PDF par mois
            </Button>
          </div>
        </div>
        <div className="rounded-xl border-2 border-slate-200 bg-white divide-y divide-slate-100" data-testid="achat-history">
          {history.length === 0 && legacy.length === 0 && <p className="p-3 text-sm text-slate-500">Aucun achat enregistré.</p>}
          {history.map((m) => (
            <div key={m.id} className="p-3 flex items-center gap-3" data-testid={`achat-row-${m.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-800">{siteName(m.site)} <span className="text-xs font-data text-slate-400 ml-1">{fmtDateTime(m.date)}</span></div>
                <div className="text-sm font-data text-slate-600">{(m.lines || []).map((l) => `${l.packaging_type}: ${fmtPiles(l.quantity, ps(l.packaging_type), true)}`).join(" · ")}</div>
                <div className="text-xs text-slate-400">{m.created_by_name}</div>
              </div>
              {(user?.role === "admin" || m.can_edit) && (
                <Button size="icon" variant="ghost" className="h-9 w-9 text-red-600" onClick={() => del(m)}
                  title="Supprimer l'achat" aria-label="Supprimer l'achat" data-testid={`delete-achat-${m.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
          {legacy.map((l, k) => (
            <div key={`legacy-${k}`} className="p-3 text-sm text-slate-500" data-testid={`legacy-initial-${k}`}>
              <span className="text-[10px] font-bold uppercase bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded mr-2">Stock initial (ancien)</span>
              {siteName(l.site)} — {l.packaging_type}: {fmtPiles(l.quantity, ps(l.packaging_type), true)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function InventoryTab() {
  const [sites, setSites] = useState([]);
  const [types, setTypes] = useState([]);
  const [map, setMap] = useState({});

  const [orig, setOrig] = useState({});
  const [dirty, setDirty] = useState({});
  const load = async () => {
    const s = (await api.get("/stocks")).data;
    setSites(s.sites); setTypes(s.types);
    const inv = (await api.get("/inventory")).data;
    const ps = Object.fromEntries(s.types.map((t) => [t.code, t.pile_size || 1]));
    const m = {};
    const o = {};
    inv.forEach((i) => {
      const k = `${i.site}|${i.packaging_type}|${i.state}`;
      o[k] = i.quantity;
      m[k] = Math.round((i.quantity / (ps[i.packaging_type] || 1)) * 10) / 10;
    });
    setOrig(o); setDirty({}); setMap(m);
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => { setMap((p) => ({ ...p, [k]: Math.max(0, parseInt(v || "0", 10)) })); setDirty((d) => ({ ...d, [k]: true })); };
  const psOf = (code) => (types.find((t) => t.code === code) || {}).pile_size || 1;

  const save = async () => {
    const lines = Object.entries(map)
      .map(([k, q]) => {
        const [site, packaging_type, state] = k.split("|");
        const quantity = dirty[k] || orig[k] == null ? Math.round(Number(q) * psOf(packaging_type)) : orig[k];
        return { site, packaging_type, state, quantity };
      })
      .filter((l) => l.quantity > 0);
    try {
      await api.post("/inventory", lines);
      toast.success("Inventaire initial enregistré.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">Saisie du stock réel de départ <strong>en piles</strong> par site, type et état. Les mouvements s'ajoutent ensuite à cette base.</p>
      {sites.map((s) => (
        <div key={s.code} className="rounded-xl border-2 border-slate-200 bg-white p-4">
          <h3 className="font-display font-bold text-slate-900 mb-3">{s.name}</h3>
          <div className="space-y-2">
            {types.map((t) => (
              <div key={t.code} className="grid grid-cols-3 gap-2 items-center">
                <span className="text-sm font-semibold text-slate-700">{t.label} <span className="block text-[10px] font-normal text-slate-400">piles · 1 pile = {fmt(t.pile_size || 1)}</span></span>
                {STATES.map((st) => {
                  const k = `${s.code}|${t.code}|${st.k}`;
                  return (
                    <div key={st.k}>
                      <Label className="text-xs text-slate-400">{st.label}</Label>
                      <Input type="number" min="0" value={map[k] ?? ""} onChange={(e) => set(k, e.target.value)}
                        className="h-11 font-data" data-testid={`inv-${k}`} />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      ))}
      <Button onClick={save} className="h-12 bg-blue-800 hover:bg-blue-900 text-white font-bold tactile" data-testid="save-inventory-button">
        <Save className="h-4 w-4 mr-2" /> Enregistrer l'inventaire
      </Button>
    </div>
  );
}

function TypesTab() {
  const [types, setTypes] = useState([]);
  const [draft, setDraft] = useState({ code: "", label: "", unit: "unités", seuil: 0 });
  const load = async () => setTypes((await api.get("/packaging-types")).data);
  useEffect(() => { load(); }, []);

  const save = async (t) => {
    try { await api.put(`/packaging-types/${t.code}`, t); toast.success("Type enregistré."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const add = async () => {
    if (!draft.code || !draft.label) return toast.error("Code et libellé requis.");
    try { await api.post("/packaging-types", { ...draft, seuil: Number(draft.seuil) }); setDraft({ code: "", label: "", unit: "unités", seuil: 0 }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (code) => { await api.delete(`/packaging-types/${code}`); load(); };

  return (
    <div className="space-y-3">
      {types.map((t, i) => (
        <div key={t.code} className="rounded-xl border-2 border-slate-200 bg-white p-3 flex flex-wrap items-end gap-2">
          <div className="w-16"><Label className="text-xs">Code</Label><Input value={t.code} disabled className="h-11 font-data" /></div>
          <div className="flex-1 min-w-[120px]"><Label className="text-xs">Libellé</Label>
            <Input value={t.label} onChange={(e) => setTypes((p) => p.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className="h-11" /></div>
          <div className="w-24"><Label className="text-xs">Unité</Label>
            <Input value={t.unit} onChange={(e) => setTypes((p) => p.map((x, j) => j === i ? { ...x, unit: e.target.value } : x))} className="h-11" /></div>
          <div className="w-24"><Label className="text-xs">Seuil</Label>
            <Input type="number" value={t.seuil} onChange={(e) => setTypes((p) => p.map((x, j) => j === i ? { ...x, seuil: Number(e.target.value) } : x))} className="h-11 font-data" data-testid={`seuil-${t.code}`} /></div>
          <Button size="icon" className="h-11 w-11 bg-blue-800" onClick={() => save(t)} data-testid={`save-type-${t.code}`} title="Enregistrer le type" aria-label={`Enregistrer le type ${t.label}`}><Save className="h-4 w-4" /></Button>
          <Button size="icon" variant="outline" className="h-11 w-11 text-red-600" onClick={() => del(t.code)} title="Supprimer le type" aria-label={`Supprimer le type ${t.label}`} data-testid={`delete-type-${t.code}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <div className="rounded-xl border-2 border-dashed border-slate-300 p-3 flex flex-wrap items-end gap-2">
        <div className="w-20"><Label className="text-xs">Code</Label><Input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })} className="h-11" data-testid="new-type-code" /></div>
        <div className="flex-1 min-w-[120px]"><Label className="text-xs">Libellé</Label><Input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} className="h-11" data-testid="new-type-label" /></div>
        <div className="w-24"><Label className="text-xs">Unité</Label><Input value={draft.unit} onChange={(e) => setDraft({ ...draft, unit: e.target.value })} className="h-11" /></div>
        <div className="w-24"><Label className="text-xs">Seuil</Label><Input type="number" value={draft.seuil} onChange={(e) => setDraft({ ...draft, seuil: e.target.value })} className="h-11" /></div>
        <Button className="h-11 bg-green-600 hover:bg-green-700" onClick={add} data-testid="add-type-button"><Plus className="h-4 w-4 mr-1" /> Ajouter</Button>
      </div>
    </div>
  );
}

const SITE_KINDS = [
  { value: "production", label: "Ferme de production" },
  { value: "lavage", label: "Centre de lavage" },
  { value: "destination", label: "Site destinataire" },
  { value: "distribution", label: "Distribution (répartition)" },
];

function SitesTab() {
  const [sites, setSites] = useState([]);
  const [types, setTypes] = useState([]);
  const [draft, setDraft] = useState({ code: "", name: "", kind: "production" });
  const load = async () => {
    setSites((await api.get("/sites")).data);
    setTypes((await api.get("/packaging-types")).data);
  };
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!draft.code || !draft.name) return toast.error("Code et nom requis.");
    try { await api.post("/sites", { ...draft, code: draft.code.toUpperCase() }); setDraft({ code: "", name: "", kind: "production" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (code) => {
    if (!window.confirm("Supprimer ce site ?")) return;
    await api.delete(`/sites/${code}`); load();
  };
  const setSeuil = (code, pt, v) =>
    setSites((p) => p.map((s) => s.code === code
      ? { ...s, seuils_piles: { ...(s.seuils_piles || {}), [pt]: Number(v || 0) } } : s));
  const setEcart = (code, v) =>
    setSites((p) => p.map((s) => s.code === code ? { ...s, ecart_pct: v === "" ? null : Number(v) } : s));
  const setSalesSeuil = (code, pt, v) =>
    setSites((p) => p.map((s) => s.code === code
      ? { ...s, seuils_sales_piles: { ...(s.seuils_sales_piles || {}), [pt]: Number(v || 0) } } : s));
  const setKind = (code, v) =>
    setSites((p) => p.map((s) => s.code === code ? { ...s, kind: v } : s));
  const saveSeuils = async (s) => {
    try {
      await api.put(`/sites/${s.code}`, { seuils_piles: s.seuils_piles || {}, ecart_pct: s.ecart_pct ?? 0, kind: s.kind, seuils_sales_piles: s.seuils_sales_piles || {} });
      toast.success(`Site ${s.name} enregistré.`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3">
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
        Définissez le <strong>seuil d'alerte par pile</strong> pour chaque site et chaque emballage.
        Une pile contient : <strong>11</strong> palettes, <strong>50</strong> intercalaires, <strong>2160</strong> alvéoles.
        L'alerte se déclenche quand le stock du site passe sous le seuil (converti en unités). Laisser 0 pour désactiver.
      </div>
      {sites.map((s) => (
        <div key={s.code} className="rounded-xl border-2 border-slate-200 bg-white p-3" data-testid={`site-card-${s.code}`}>
          <div className="flex items-center gap-3 mb-3">
            <span className="font-data font-bold text-slate-800">{s.code}</span>
            <span className="flex-1 font-semibold">{s.name}</span>
            <Select value={s.kind} onValueChange={(v) => setKind(s.code, v)}>
              <SelectTrigger className="h-9 w-52" data-testid={`site-kind-${s.code}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {SITE_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="icon" variant="outline" className="h-10 w-10 text-red-600" onClick={() => del(s.code)} title="Supprimer le site" aria-label={`Supprimer le site ${s.name}`} data-testid={`delete-site-${s.code}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
          <p className="text-xs font-bold text-slate-500 uppercase mb-2">Seuils d'alerte (en piles)</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
            {types.map((t) => {
              const piles = (s.seuils_piles || {})[t.code] || 0;
              return (
                <div key={t.code}>
                  <Label className="text-xs">{t.label} <span className="text-slate-400">(×{t.pile_size})</span></Label>
                  <Input type="number" min="0" value={piles || ""} placeholder="0"
                    onChange={(e) => setSeuil(s.code, t.code, e.target.value)}
                    className="h-11 font-data" data-testid={`seuil-pile-${s.code}-${t.code}`} />
                  <span className="text-[11px] text-slate-400 font-data">= {(piles * (t.pile_size || 1)).toLocaleString("fr-FR")} u.</span>
                </div>
              );
            })}
            <div>
              <Label className="text-xs">Seuil d'écart (%)</Label>
              <Input type="number" min="0" value={s.ecart_pct ?? ""} placeholder="20 (défaut)"
                onChange={(e) => setEcart(s.code, e.target.value)}
                className="h-11 font-data" data-testid={`ecart-pct-${s.code}`} />
              <span className="text-[11px] text-slate-400">déclarations de stock</span>
            </div>
            <Button className="h-11 bg-blue-800 hover:bg-blue-900 font-bold" onClick={() => saveSeuils(s)} data-testid={`save-seuils-${s.code}`}>
              <Save className="h-4 w-4 mr-1" /> Enregistrer
            </Button>
          </div>
          {s.code === "ANNEZIN" && (
            <div className="mt-3 pt-3 border-t border-slate-200">
              <p className="text-xs font-bold text-red-600 uppercase mb-2">Seuil « sales à expédier vers DDO » (en piles)</p>
              <div className="grid grid-cols-3 gap-2">
                {types.map((t) => {
                  const hp = (s.seuils_sales_piles || {})[t.code] || 0;
                  return (
                    <div key={t.code}>
                      <Label className="text-xs">{t.label}</Label>
                      <Input type="number" min="0" value={hp || ""} placeholder="0 (aucune)"
                        onChange={(e) => setSalesSeuil(s.code, t.code, e.target.value)}
                        className="h-11 font-data" data-testid={`seuil-sales-${s.code}-${t.code}`} />
                    </div>
                  );
                })}
              </div>
              <span className="text-[11px] text-slate-400">Alerte quand le stock sale ANNEZIN atteint ce seuil.</span>
            </div>
          )}
        </div>
      ))}
      <div className="rounded-xl border-2 border-dashed border-slate-300 p-3 flex flex-wrap items-end gap-2">
        <div className="w-24"><Label className="text-xs">Code</Label><Input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} className="h-11" data-testid="new-site-code" /></div>
        <div className="flex-1 min-w-[120px]"><Label className="text-xs">Nom</Label><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="h-11" data-testid="new-site-name" /></div>
        <div className="w-52"><Label className="text-xs">Type</Label>
          <Select value={draft.kind} onValueChange={(v) => setDraft({ ...draft, kind: v })}>
            <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SITE_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button className="h-11 bg-green-600 hover:bg-green-700" onClick={add} data-testid="add-site-button"><Plus className="h-4 w-4 mr-1" /> Ajouter</Button>
      </div>
    </div>
  );
}

function UserEditDialog({ open, onOpenChange, target, modules, sites, onSaved }) {
  const { user: me, can } = useAuth();
  const meIsAdmin = me?.role === "admin";
  const canPerms = can("users", "admin");
  const isNew = !target;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("annezin");
  const [site, setSite] = useState("none");
  const [active, setActive] = useState(true);
  const [perms, setPerms] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDirty(false);
    if (target) {
      setName(target.name || ""); setEmail(target.email || ""); setPassword("");
      setRole(target.role || "annezin"); setSite(target.site || "none");
      setActive(target.active !== false); setPerms(target.permissions || {});
    } else {
      setName(""); setEmail(""); setPassword(""); setRole("annezin"); setSite("none");
      setActive(true); setPerms({});
    }
  }, [open, target]);

  const markDirty = (fn) => (...a) => { setDirty(true); fn(...a); };

  const close = (o) => {
    if (!o && dirty && !window.confirm("Des modifications ne sont pas enregistrées. Quitter quand même ?")) return;
    onOpenChange(o);
  };

  const save = async () => {
    if (isNew && (!name || !email || !password)) return toast.error("Nom, email et mot de passe requis.");
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/users", { name, email, password, role, site: site === "none" ? null : site, permissions: canPerms ? perms : null, active });
        toast.success("Utilisateur créé.");
      } else {
        await api.put(`/users/${target.id}`, { name, role, site: site === "none" ? null : site, active, ...(password ? { password } : {}) });
        if (canPerms) await api.put(`/users/${target.id}/permissions`, { permissions: perms });
        toast.success("Utilisateur et permissions enregistrés.");
      }
      setDirty(false);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const isSuper = target?.is_super_admin;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="user-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{isNew ? "Nouvel utilisateur" : `Fiche : ${target.name}`}</DialogTitle>
          <DialogDescription>{isNew ? "Créer un compte et définir ses accès." : "Modifier les informations et les permissions."}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Nom</Label><Input value={name} onChange={markDirty((e) => setName(e.target.value))} className="h-11" data-testid="new-user-name" /></div>
            <div><Label>Email</Label><Input value={email} disabled={!isNew} onChange={markDirty((e) => setEmail(e.target.value))} className="h-11" data-testid="new-user-email" /></div>
            <div><Label>{isNew ? "Mot de passe" : "Nouveau mot de passe (optionnel)"}</Label>
              <Input type="text" value={password} onChange={markDirty((e) => setPassword(e.target.value))} className="h-11" data-testid="new-user-password" /></div>
            <div><Label>Rôle (modèle)</Label>
              <Select value={role} onValueChange={markDirty(setRole)} disabled={isSuper}>
                <SelectTrigger className="h-11" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {meIsAdmin && <SelectItem value="admin">Super administrateur</SelectItem>}
                  <SelectItem value="annezin">ANNEZIN</SelectItem>
                  <SelectItem value="ddo">DDO</SelectItem>
                  <SelectItem value="ferme_production">FERME PRODUCTION</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Site</Label>
              <Select value={site || "none"} onValueChange={markDirty((v) => setSite(v))}>
                <SelectTrigger className="h-11" data-testid="new-user-site"><SelectValue placeholder="Aucun site" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun site</SelectItem>
                  {sites.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch checked={active} onCheckedChange={markDirty(setActive)} disabled={isSuper} data-testid="user-active-switch" />
              <span className="font-semibold text-slate-700">{active ? "Compte actif" : "Compte désactivé"}</span>
            </div>
          </div>

          <div className="pt-2">
            <h4 className="font-display font-bold text-slate-900 mb-1">Permissions et accès</h4>
            {isSuper ? (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                Ce compte est un <strong>super administrateur</strong> : il dispose de tous les droits sur l'ensemble du logiciel.
              </div>
            ) : !canPerms ? (
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600" data-testid="perms-readonly-note">
                Les permissions sont celles du rôle choisi. Seul un administrateur peut les personnaliser.
              </div>
            ) : (
              <PermissionMatrix modules={modules} value={perms} onChange={markDirty(setPerms)} />
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} className="h-12">Annuler</Button>
          <Button onClick={save} disabled={saving} className="h-12 bg-blue-800 hover:bg-blue-900 text-white font-bold" data-testid="save-permissions-button">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UsersTab() {
  const { user: me } = useAuth();
  const meIsAdmin = me?.role === "admin";
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [modules, setModules] = useState([]);
  const [editing, setEditing] = useState(null); // {} for new, user for edit, null closed
  const load = async () => {
    setUsers((await api.get("/users")).data);
    setSites((await api.get("/sites")).data);
    setModules((await api.get("/modules")).data);
  };
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!window.confirm("Supprimer cet utilisateur ? Son historique de saisies est conservé.")) return;
    try { await api.delete(`/users/${id}`); load(); toast.success("Utilisateur supprimé."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const permCount = (u) => Object.values(u.permissions || {}).reduce((n, c) => n + Object.values(c).filter(Boolean).length, 0);

  return (
    <div className="space-y-3">
      <Button className="h-12 bg-green-600 hover:bg-green-700 text-white font-bold w-full sm:w-auto" onClick={() => setEditing({})} data-testid="open-new-user">
        <Plus className="h-4 w-4 mr-1" /> Nouvel utilisateur
      </Button>

      {users.map((u) => (
        <div key={u.id} className={`rounded-xl border-2 bg-white p-3 flex flex-wrap items-center gap-2 ${u.active === false ? "border-slate-200 opacity-70" : "border-slate-200"}`} data-testid={`user-row-${u.email}`}>
          <div className="flex-1 min-w-[140px]">
            <div className="font-semibold text-slate-800 flex items-center gap-2">
              {u.name}
              {u.is_super_admin && <span className="text-[10px] font-bold uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Super admin</span>}
            </div>
            <div className="text-xs text-slate-500 font-data">{u.email}</div>
            <div className="text-xs text-slate-400" data-testid={`user-site-${u.email}`}>
              {u.site ? ((sites.find((x) => x.code === u.site) || {}).name || u.site) : "Aucun site"}
            </div>
          </div>
          <span className="text-xs font-bold uppercase bg-blue-100 text-blue-800 px-2 py-1 rounded">{u.role}</span>
          {u.active === false
            ? <span className="text-xs font-bold uppercase bg-slate-200 text-slate-600 px-2 py-1 rounded">Désactivé</span>
            : <span className="text-xs font-bold uppercase bg-green-100 text-green-800 px-2 py-1 rounded">Actif</span>}
          <span className="text-xs text-slate-400 hidden sm:inline">{permCount(u)} droits</span>
          {(meIsAdmin || !u.is_super_admin) && (
            <>
              <Button size="sm" variant="outline" className="h-9 border-2" onClick={() => setEditing(u)} data-testid={`edit-user-${u.email}`} title="Modifier l'utilisateur" aria-label={`Modifier ${u.name}`}>
                <Pencil className="h-4 w-4 mr-1" /> Modifier
              </Button>
              {u.id !== me?.id && (
                <Button size="icon" variant="outline" className="h-9 w-9 text-red-600" onClick={() => del(u.id)} data-testid={`delete-user-${u.email}`} title="Supprimer l'utilisateur" aria-label={`Supprimer ${u.name}`}><Trash2 className="h-4 w-4" /></Button>
              )}
            </>
          )}
        </div>
      ))}

      <UserEditDialog
        open={editing !== null}
        onOpenChange={(o) => { if (!o) setEditing(null); }}
        target={editing && editing.id ? editing : null}
        modules={modules}
        sites={sites}
        onSaved={load}
      />
    </div>
  );
}

function AuditValue({ label, value, tone }) {
  if (value == null) return null;
  return (
    <div className="min-w-0 flex-1">
      <p className={`text-[10px] font-bold uppercase ${tone}`}>{label}</p>
      <pre className="text-[11px] font-data bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all max-h-48">{JSON.stringify(value, null, 1)}</pre>
    </div>
  );
}

function AuditFilters({ filters, value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end" data-testid="audit-filters">
      <div className="col-span-2 sm:col-span-1">
        <Label className="text-xs">Utilisateur</Label>
        <Select value={value.user_id || "all"} onValueChange={(v) => set("user_id", v === "all" ? "" : v)}>
          <SelectTrigger className="h-11" data-testid="audit-filter-user"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les utilisateurs</SelectItem>
            {(filters.users || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2 sm:col-span-1">
        <Label className="text-xs">Action</Label>
        <Select value={value.action || "all"} onValueChange={(v) => set("action", v === "all" ? "" : v)}>
          <SelectTrigger className="h-11" data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les actions</SelectItem>
            {(filters.actions || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">Du</Label>
        <DatePickerFr value={value.date_from || ""} onChange={(v) => onChange({ ...value, date_from: v, ...(value.date_to && v && v > value.date_to ? { date_to: "" } : {}) })} testid="audit-filter-from" />
      </div>
      <div>
        <Label className="text-xs">Au</Label>
        <DatePickerFr value={value.date_to || ""} onChange={(v) => set("date_to", v)} testid="audit-filter-to" minDate={value.date_from || undefined} />
      </div>
      <Button variant="outline" className="h-11 border-2 col-span-2 sm:col-span-1" onClick={() => onChange({})} data-testid="audit-filter-reset">
        Réinitialiser
      </Button>
    </div>
  );
}

function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [open, setOpen] = useState(null);
  const [filters, setFilters] = useState({ users: [], actions: [] });
  const [f, setF] = useState({});
  useEffect(() => { api.get("/audit/filters").then(({ data }) => setFilters(data)); }, []);
  const exportCsv = async () => {
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const res = await api.get("/export/audit.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "journal.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/audit", { params }).then(({ data }) => setLogs(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [f]);
  return (
    <div className="space-y-3">
    <AuditFilters filters={filters} value={f} onChange={setF} />
    <div className="flex items-center gap-2">
    <Button variant="outline" className="h-10 border-2 order-2 ml-auto" onClick={exportCsv} data-testid="export-audit-csv-button">
      <FileDown className="h-4 w-4 mr-2" /> Exporter en CSV
    </Button>
    <p className="text-xs text-slate-500" data-testid="audit-count">{logs.length} entrée{logs.length > 1 ? "s" : ""}{logs.length >= 500 ? " (500 max. affichées, l'export contient tout)" : ""}</p>
    </div>
    <div className="rounded-xl border-2 border-slate-200 bg-white divide-y divide-slate-100">
      {logs.length === 0 && <p className="p-4 text-slate-500 text-sm">Aucune entrée.</p>}
      {logs.map((l) => (
        <div key={l.id} className="p-3" data-testid={`audit-row-${l.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-data text-xs bg-slate-100 px-2 py-0.5 rounded font-bold">{l.action}</span>
            <span className="text-sm font-semibold text-slate-700">{l.user_name}</span>
            <span className="text-xs text-slate-400 ml-auto font-data">{fmtDateTime(l.at)}</span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-sm text-slate-500 flex-1">{l.label}</p>
            {(l.before != null || l.after != null) && (
              <button type="button" onClick={() => setOpen(open === l.id ? null : l.id)}
                className="text-xs font-semibold text-blue-700 hover:underline" data-testid={`audit-toggle-${l.id}`}>
                {open === l.id ? "Masquer" : "Avant / après"}
              </button>
            )}
          </div>
          {open === l.id && (
            <div className="flex flex-col sm:flex-row gap-2 mt-2" data-testid={`audit-detail-${l.id}`}>
              <AuditValue label="Avant" value={l.before} tone="text-red-600" />
              <AuditValue label="Après" value={l.after} tone="text-green-700" />
            </div>
          )}
        </div>
      ))}
    </div>
    </div>
  );
}

function StockDeclarationsTab() {
  const [decls, setDecls] = useState([]);
  const [status, setStatus] = useState(null);
  const [types, setTypes] = useState([]);
  const [chartFerme, setChartFerme] = useState("");
  const [chartType, setChartType] = useState("");
  const load = async () => {
    const [d, s, t] = await Promise.all([
      api.get("/stock-declarations"),
      api.get("/stock-declarations/status"),
      api.get("/packaging-types"),
    ]);
    setDecls(d.data); setStatus(s.data); setTypes(t.data);
  };
  useEffect(() => { load(); }, []);

  const cols = types.length ? types : [{ code: "PAL", label: "Palettes" }, { code: "INT", label: "Intercalaires" }, { code: "ALV", label: "Alvéoles" }];
  useEffect(() => {
    if (!chartType && cols.length) setChartType(cols[0].code);
  }, [cols, chartType]);
  useEffect(() => {
    if (!chartFerme && status?.fermes?.length) setChartFerme(status.fermes[0].site);
  }, [status, chartFerme]);

  const lineOf = (d, code) => (d.lines || []).find((x) => x.packaging_type === code);
  const pileOf = (d, code) => { const l = lineOf(d, code); return l ? l.piles : 0; };
  const threshold = status?.ecart_threshold_pct ?? 20;

  const fridayLabel = (() => {
    return status?.friday ? fmtDay(status.friday) : "";
  })();
  const when = (iso) => fmtDateTime(iso);
  const fmtExpected = (iso) => fmtDay(iso);

  const download = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const chartData = decls
    .filter((d) => d.active && d.site === chartFerme)
    .map((d) => ({ week: d.week_friday, piles: pileOf(d, chartType) }))
    .sort((a, b) => a.week.localeCompare(b.week));

  return (
    <div className="space-y-6" data-testid="admin-declarations-tab">
      <div className="flex justify-end gap-2">
        <Button variant="outline" className="h-11 border-2 tactile" onClick={() => download("/export/declarations.csv", "declarations.csv")} data-testid="export-decl-csv-button">
          <FileDown className="h-4 w-4 mr-2" /> CSV
        </Button>
        <Button variant="outline" className="h-11 border-2 tactile" onClick={() => download("/export/declarations.pdf", "declarations.pdf")} data-testid="export-decl-pdf-button">
          <FileText className="h-4 w-4 mr-2" /> PDF
        </Button>
      </div>

      <div>
        <h3 className="font-display font-bold text-slate-900 mb-1">Statut de la semaine</h3>
        <p className="text-sm text-slate-500 mb-2 capitalize">📅 {fridayLabel}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {(status?.fermes || []).length === 0 && (
            <p className="text-sm text-slate-500">Aucune ferme de production enregistrée.</p>
          )}
          {(status?.fermes || []).map((f) => (
            <div key={f.site} className={`rounded-xl border-2 p-3 flex items-center justify-between gap-2 ${f.declared ? "border-green-300 bg-green-50" : "border-red-300 bg-red-50"}`} data-testid={`decl-status-${f.site}`}>
              <span className="min-w-0">
                <span className="block font-semibold text-slate-800 truncate">{f.site_name}</span>
                <span className="block text-xs text-slate-500">
                  {f.declared ? "À jour : " : "Attendue : "}
                  <span className="capitalize">{fmtExpected(f.expected_friday)}</span>
                </span>
              </span>
              <span className="flex items-center gap-1.5 shrink-0">
                {f.has_ecart && (
                  <span className="text-xs font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-800" data-testid={`decl-ecart-${f.site}`}>⚠️ Écart</span>
                )}
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${f.declared ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"}`}>
                  {f.declared ? "🟢 Déclaré" : f.late ? "🔴 EN RETARD" : "🔴 À déclarer"}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <h3 className="font-display font-bold text-slate-900">Évolution hebdomadaire (piles déclarées)</h3>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <Select value={chartFerme} onValueChange={setChartFerme}>
            <SelectTrigger className="h-11" data-testid="chart-ferme-filter"><SelectValue placeholder="Ferme" /></SelectTrigger>
            <SelectContent>
              {(status?.fermes || []).map((f) => <SelectItem key={f.site} value={f.site}>{f.site_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={chartType} onValueChange={setChartType}>
            <SelectTrigger className="h-11" data-testid="chart-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              {cols.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="rounded-xl border-2 border-slate-200 bg-white p-3" data-testid="decl-weekly-chart">
          {chartData.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">Aucune déclaration pour cette sélection.</p>
          ) : (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer minWidth={0}>
                <LineChart data={chartData} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <RTooltip formatter={(v) => [`${v} piles`, "Déclaré"]} />
                  <Line type="monotone" dataKey="piles" name="Piles" stroke="#1E3A8A" strokeWidth={2.5} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div>
        <h3 className="font-display font-bold text-slate-900 mb-2">
          Historique des déclarations
          <span className="ml-2 text-xs font-normal text-slate-400">Écart signalé selon le seuil de chaque ferme (défaut {threshold}%)</span>
        </h3>
        <div className="overflow-x-auto rounded-xl border-2 border-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-900 text-white">
                <th className="text-left px-3 py-2 font-display">Ferme</th>
                <th className="text-left px-3 py-2 font-display">Déclaré le</th>
                <th className="text-left px-3 py-2 font-display">Vendredi</th>
                <th className="text-left px-3 py-2 font-display">Utilisateur</th>
                {cols.map((c) => (
                  <th key={c.code} className="px-2 py-2 font-display text-center whitespace-nowrap">{c.label} (piles)</th>
                ))}
                <th className="px-2 py-2 font-display text-center">Écart</th>
              </tr>
            </thead>
            <tbody>
              {decls.length === 0 && (
                <tr><td colSpan={5 + cols.length} className="px-3 py-4 text-slate-500">Aucune déclaration.</td></tr>
              )}
              {decls.map((d, i) => (
                <tr key={d.id} className={`${i % 2 ? "bg-slate-50/60" : ""} ${d.active ? "" : "opacity-60"}`} data-testid={`decl-row-${d.id}`}>
                  <td className="px-3 py-2 font-semibold text-slate-800">{d.site_name}</td>
                  <td className="px-3 py-2 font-data text-slate-600 whitespace-nowrap">
                    {when(d.created_at)}
                    {!d.active && <span className="ml-1 text-[10px] font-bold uppercase bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded">Remplacée</span>}
                  </td>
                  <td className="px-3 py-2 font-data text-slate-500">{d.week_friday}</td>
                  <td className="px-3 py-2 text-slate-700">{d.user_name}</td>
                  {cols.map((c) => {
                    const l = lineOf(d, c.code);
                    const isEcart = l && l.ecart;
                    return (
                      <td key={c.code} className={`px-2 py-2 text-center font-data font-bold ${isEcart ? "text-amber-700" : "text-slate-900"}`} title={l && l.expected_piles != null ? `Attendu : ${l.expected_piles} (écart ${l.ecart_pct}%)` : undefined}>
                        {fmt(pileOf(d, c.code))}{isEcart ? " ⚠️" : ""}
                      </td>
                    );
                  })}
                  <td className="px-2 py-2 text-center">
                    {d.has_ecart
                      ? <span className="text-xs font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-800" data-testid={`decl-row-ecart-${d.id}`}>⚠️</span>
                      : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function BesoinsTab() {
  const [besoins, setBesoins] = useState([]);
  const [types, setTypes] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/besoins"), api.get("/packaging-types")])
      .then(([b, t]) => { setBesoins(b.data); setTypes(t.data); });
  }, []);
  const cols = types.length ? types : [{ code: "PAL", label: "Palettes" }, { code: "INT", label: "Intercalaires" }, { code: "ALV", label: "Alvéoles" }];
  const pileOf = (d, code) => { const l = (d.lines || []).find((x) => x.packaging_type === code); return l ? l.piles : 0; };
  const when = (iso) => fmtDateTime(iso);
  return (
    <div className="space-y-3" data-testid="admin-besoins-tab">
      <h3 className="font-display font-bold text-slate-900">Historique des besoins des fermes</h3>
      <div className="overflow-x-auto rounded-xl border-2 border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-900 text-white">
              <th className="text-left px-3 py-2 font-display">Ferme</th>
              <th className="text-left px-3 py-2 font-display">Déclaré le</th>
              <th className="text-left px-3 py-2 font-display">Utilisateur</th>
              {cols.map((c) => <th key={c.code} className="px-2 py-2 font-display text-center whitespace-nowrap">{c.label} (piles)</th>)}
              <th className="text-left px-3 py-2 font-display">Commentaire</th>
              <th className="px-2 py-2 font-display text-center">Statut</th>
            </tr>
          </thead>
          <tbody>
            {besoins.length === 0 && <tr><td colSpan={5 + cols.length} className="px-3 py-4 text-slate-500">Aucun besoin déclaré.</td></tr>}
            {besoins.map((d, i) => (
              <tr key={d.id} className={`${i % 2 ? "bg-slate-50/60" : ""} ${d.active ? "" : "opacity-60"}`} data-testid={`besoin-row-${d.id}`}>
                <td className="px-3 py-2 font-semibold text-slate-800">{d.site_name}</td>
                <td className="px-3 py-2 font-data text-slate-600 whitespace-nowrap">{when(d.created_at)}</td>
                <td className="px-3 py-2 text-slate-700">{d.user_name}</td>
                {cols.map((c) => <td key={c.code} className="px-2 py-2 text-center font-data font-bold text-slate-900">{fmt(pileOf(d, c.code))}</td>)}
                <td className="px-3 py-2 text-slate-600 max-w-[180px] truncate" title={d.commentaire || ""}>{d.commentaire || "—"}</td>
                <td className="px-2 py-2 text-center">
                  {d.active ? <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Actif</span>
                            : <span className="text-[10px] font-bold uppercase bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded">Remplacé</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, can } = useAuth();
  const show = {
    inventory: can("inventory", "view") || can("inventory", "edit"),
    initial: can("initial_stock", "view") || can("initial_stock", "edit"),
    types: can("types", "view") || can("types", "edit"),
    sites: can("sites", "view") || can("sites", "edit"),
    users: ["admin", "annezin"].includes(user?.role) && can("users", "view"),
    audit: can("audit", "view"),
    declarations: can("stock_declaration", "view"),
    besoins: can("besoin", "view"),
  };
  const first = Object.keys(show).find((k) => show[k]);

  if (!first) {
    return (
      <AppShell>
        <div className="rounded-xl border-2 border-dashed border-slate-300 bg-white p-8 text-center text-slate-500">
          Vous n'avez accès à aucun module d'administration.
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <h1 className="font-display font-extrabold text-2xl text-slate-900 mb-4 uppercase tracking-tight">Administration</h1>
      <Tabs defaultValue={first}>
        <TabsList className="flex flex-wrap h-auto bg-slate-200 gap-1">
          {show.inventory && <TabsTrigger value="inventory" className="font-bold" data-testid="admin-tab-inventory">Inventaire</TabsTrigger>}
          {show.initial && <TabsTrigger value="initial" className="font-bold" data-testid="admin-tab-initial">Achat d'emballages neufs</TabsTrigger>}
          {show.types && <TabsTrigger value="types" className="font-bold" data-testid="admin-tab-types">Types & seuils</TabsTrigger>}
          {show.sites && <TabsTrigger value="sites" className="font-bold" data-testid="admin-tab-sites">Sites</TabsTrigger>}
          {show.users && <TabsTrigger value="users" className="font-bold" data-testid="admin-tab-users">Utilisateurs</TabsTrigger>}
          {show.declarations && <TabsTrigger value="declarations" className="font-bold" data-testid="admin-tab-declarations">Déclarations</TabsTrigger>}
          {show.besoins && <TabsTrigger value="besoins" className="font-bold" data-testid="admin-tab-besoins">Besoins</TabsTrigger>}
          {show.audit && <TabsTrigger value="audit" className="font-bold" data-testid="admin-tab-audit">Journal</TabsTrigger>}
        </TabsList>
        {show.inventory && (
          <TabsContent value="inventory" className="mt-4 space-y-6">
            <InventoryTab />
            {user?.role === "admin" && <ResetZone />}
          </TabsContent>
        )}
        {show.initial && <TabsContent value="initial" className="mt-4"><AchatNeufsTab /></TabsContent>}
        {show.types && <TabsContent value="types" className="mt-4"><TypesTab /></TabsContent>}
        {show.sites && <TabsContent value="sites" className="mt-4"><SitesTab /></TabsContent>}
        {show.users && <TabsContent value="users" className="mt-4"><UsersTab /></TabsContent>}
        {show.declarations && <TabsContent value="declarations" className="mt-4"><StockDeclarationsTab /></TabsContent>}
        {show.besoins && <TabsContent value="besoins" className="mt-4"><BesoinsTab /></TabsContent>}
        {show.audit && <TabsContent value="audit" className="mt-4"><AuditTab /></TabsContent>}
      </Tabs>
    </AppShell>
  );
}
